import { ARTICLE_PICTURE_CAPTION_ATTRIBUTE, ARTICLE_PICTURE_MIN_WIDTH_PROPERTY, articlePictureMinWidthOf, articlePictureWidthOf } from '@bms2/shared';

/**
 * The article editor's picture tools (piece 30; T372; decisions 1242, 1243):
 * which picture a selection holds, what its bar shows, and the picture's own
 * line that its alignment is set on. 🔨 Each one reads or rewrites the
 * surface's own nodes and nothing else, so the markup it leaves is what the
 * article's allowed table keeps: a picture with its width, on a line whose
 * alignment the article already keeps.
 */

/** Where a picture stands on its line: the three the bar offers, left meaning no alignment written at all. */
export type PictureAlignment = 'left' | 'center' | 'right';

/** What the picture bar shows of a picture: its width in percent or none, its desired minimum in pixels or none (decision 1942), its alignment, its words, and its caption (decision 1476). */
export interface PictureFacts {
  width: number | null;
  minWidth: number | null;
  align: PictureAlignment;
  alt: string;
  caption: string;
}

/** The blocks a picture's line can be: the ones a paragraph can split into. */
const LINE_BLOCKS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'PRE', 'BLOCKQUOTE']);
/** The blocks a picture can stand in without being the line itself: a list item, a cell. */
const CONTAINERS = new Set(['LI', 'TD', 'TH']);

/** The picture a selection holds whole and alone, with no word beside it: a click on it, or Shift and an arrow from beside it. */
export function selectedPicture(surface: HTMLElement, selection: Selection | null): HTMLImageElement | null {
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  if (range.toString().trim() !== '') return null;
  const held = Array.from(surface.querySelectorAll('img')).filter((img) => range.intersectsNode(img));
  return held.length === 1 ? held[0]! : null;
}

/** The picture standing directly before or after a caret, the one after it first; Alt+F10 from there reaches its bar (decision 1243). */
export function pictureBeside(surface: HTMLElement, selection: Selection | null): HTMLImageElement | null {
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;
  const at = selection.anchorNode;
  const offset = selection.anchorOffset;
  if (!at || !surface.contains(at)) return null;
  let before: Node | null = null;
  let after: Node | null = null;
  if (at.nodeType === Node.TEXT_NODE) {
    if (offset === 0) before = at.previousSibling;
    if (offset === (at.textContent ?? '').length) after = at.nextSibling;
  } else {
    before = at.childNodes[offset - 1] ?? null;
    after = at.childNodes[offset] ?? null;
  }
  if (after instanceof HTMLImageElement) return after;
  if (before instanceof HTMLImageElement) return before;
  return null;
}

/** The block a picture stands in, inside the surface, or null where it stands straight in the surface. */
function blockOf(surface: HTMLElement, picture: HTMLImageElement): HTMLElement | null {
  let at: HTMLElement | null = picture.parentElement;
  while (at && at !== surface) {
    if (LINE_BLOCKS.has(at.tagName) || CONTAINERS.has(at.tagName)) return at;
    at = at.parentElement;
  }
  return null;
}

/** What the bar shows of a picture: the width it keeps, the alignment of the block it stands in, its words and its caption. */
export function pictureFactsOf(surface: HTMLElement, picture: HTMLImageElement): PictureFacts {
  const width = picture.style.width.endsWith('%') ? articlePictureWidthOf(picture.style.width) : null;
  const minimum = picture.style.getPropertyValue(ARTICLE_PICTURE_MIN_WIDTH_PROPERTY);
  const minWidth = /px\s*$/i.test(minimum) ? articlePictureMinWidthOf(minimum) : null;
  const aligned = blockOf(surface, picture)?.style.textAlign ?? '';
  return { width, minWidth, align: aligned === 'center' || aligned === 'right' ? aligned : 'left', alt: picture.getAttribute('alt') ?? '', caption: picture.getAttribute(ARTICLE_PICTURE_CAPTION_ATTRIBUTE) ?? '' };
}

/** Whether a node holds anything a reader sees: a word, or an element that draws on its own. */
function holdsAnything(node: Node): boolean {
  if ((node.textContent ?? '').trim() !== '') return true;
  return node instanceof Element || node instanceof DocumentFragment ? node.querySelector('img, table, hr') !== null : false;
}

/**
 * 🔒 The picture's own line (decision 1243), made where it has none: a picture
 * alone in a paragraph already has one; a picture among words moves onto a new
 * paragraph between the words before it and the words after it, every word
 * kept; a picture in a list item, a cell or straight in the surface is wrapped
 * in a paragraph where it stands. The line is what its alignment is set on.
 */
export function pictureLine(surface: HTMLElement, picture: HTMLImageElement): HTMLElement {
  const block = blockOf(surface, picture);
  // A link around the picture moves with it; any other inline element around it is split by the range, its words kept on either side.
  const unit: HTMLElement = picture.parentElement?.tagName === 'A' && picture.parentElement.childNodes.length === 1 ? picture.parentElement : picture;
  if (block && LINE_BLOCKS.has(block.tagName)) {
    if (block.tagName === 'P' && unit.parentElement === block && Array.from(block.childNodes).every((node) => node === unit || !holdsAnything(node))) return block;
    const afterRange = document.createRange();
    afterRange.setStartAfter(unit);
    afterRange.setEnd(block, block.childNodes.length);
    const tail = afterRange.extractContents();
    const line = document.createElement('p');
    block.after(line);
    line.appendChild(unit);
    if (holdsAnything(tail)) {
      const after = block.cloneNode(false) as HTMLElement;
      after.appendChild(tail);
      line.after(after);
    }
    if (!holdsAnything(block)) block.remove();
    return line;
  }
  const line = document.createElement('p');
  unit.replaceWith(line);
  line.appendChild(unit);
  return line;
}

/** The alignment set on the picture's own line: Left writes none, as a line with no alignment already reads left. */
export function alignPicture(surface: HTMLElement, picture: HTMLImageElement, align: PictureAlignment): void {
  const line = pictureLine(surface, picture);
  if (align === 'left') line.style.removeProperty('text-align');
  else line.style.textAlign = align;
  if (!line.getAttribute('style')) line.removeAttribute('style');
}

/** 🔒 The caption a picture keeps (decision 1476), or none where it is emptied; never copied into its alt text. */
export function captionPicture(picture: HTMLImageElement, caption: string): void {
  if (caption.trim() === '') picture.removeAttribute(ARTICLE_PICTURE_CAPTION_ATTRIBUTE);
  else picture.setAttribute(ARTICLE_PICTURE_CAPTION_ATTRIBUTE, caption);
}

/** The width a picture keeps, or none; the bar only ever hands it a percent the article keeps. */
export function sizePicture(picture: HTMLImageElement, percent: number | null): void {
  if (percent === null) picture.style.removeProperty('width');
  else picture.style.width = `${percent}%`;
  if (!picture.getAttribute('style')) picture.removeAttribute('style');
}

/** 🔒 The minimum a picture keeps on screen, 100 to 1200 pixels, or none, where it takes the 200-pixel default (decision 1942). */
export function minWidthPicture(picture: HTMLImageElement, pixels: number | null): void {
  if (pixels === null) picture.style.removeProperty(ARTICLE_PICTURE_MIN_WIDTH_PROPERTY);
  else picture.style.setProperty(ARTICLE_PICTURE_MIN_WIDTH_PROPERTY, `${pixels}px`);
  if (!picture.getAttribute('style')) picture.removeAttribute('style');
}

/** The picture taken out of the words (decision 1243); its line, left empty, keeps a line break so the caret can stand there. */
export function removePicture(picture: HTMLImageElement): Node | null {
  const line = picture.parentElement;
  picture.remove();
  if (line && !holdsAnything(line) && line.childNodes.length === 0) line.innerHTML = '<br>';
  return line;
}
